import React, { useState } from 'react';
import { UserCircleIcon, SpinnerIcon } from '../../../components/Icons';
import { CIEN_CUADRAS_BUTTON_BLUE } from '../../../constants';
import { requestPasswordRecovery, isValidEmailFormat } from '../../../services/authRecoveryService';

interface PasswordResetSectionProps {
  onPasswordReset?: (email: string) => Promise<boolean>;
  onSuccess?: (msg: string) => void;
  onError?: (err: string) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

export const PasswordResetSection: React.FC<PasswordResetSectionProps> = ({
  onPasswordReset,
  onSuccess,
  onError,
  onCancel,
  isLoading = false,
}) => {
  const [resetEmail, setResetEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const cleanEmail = resetEmail.trim().toLowerCase();
    if (!cleanEmail || isSubmitting || isLoading) return;

    if (!isValidEmailFormat(cleanEmail)) {
      const msg = 'Por favor ingresa un formato de correo electrónico válido.';
      setLocalError(msg);
      onError?.(msg);
      return;
    }

    setLocalError(null);
    setIsSubmitting(true);

    try {
      if (onPasswordReset) {
        const ok = await onPasswordReset(cleanEmail);
        if (!ok) {
          setLocalError('El correo no se encuentra registrado o hubo un error al enviar.');
        }
      } else {
        const result = await requestPasswordRecovery(cleanEmail);
        if (!result.success) {
          const err = result.error || 'Error al procesar solicitud.';
          setLocalError(err);
          onError?.(err);
        } else if (result.message) {
          onSuccess?.(result.message);
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const isDisabled = isSubmitting || isLoading || resetEmail.trim() === '';

  return (
    <form onSubmit={handleSubmit} className="mt-6 border-t border-gray-200 pt-6 space-y-4">
      <h3 className="text-lg font-medium text-center text-gray-700">Restablecer Contraseña</h3>
      <p className="text-xs text-gray-500 text-center">
        Ingresa tu correo y validaremos tu registro para enviarte el enlace de recuperación.
      </p>

      {localError && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-md text-xs text-red-700 text-center">
          {localError}
        </div>
      )}

      <div>
        <label htmlFor="passwordResetEmail" className="sr-only">Correo Electrónico</label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <UserCircleIcon className="h-5 w-5 text-gray-400" />
          </div>
          <input
            id="passwordResetEmail"
            name="passwordResetEmail"
            type="email"
            autoComplete="email"
            required
            value={resetEmail}
            onChange={(e) => {
              setResetEmail(e.target.value);
              if (localError) setLocalError(null);
            }}
            className="appearance-none block w-full px-3 py-2 pl-10 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
            placeholder="Correo Electrónico Registrado"
            disabled={isSubmitting || isLoading}
          />
        </div>
      </div>
      <button
        type="submit"
        disabled={isDisabled}
        className="w-full flex items-center justify-center py-2.5 px-4 rounded-md shadow-sm text-sm font-medium text-white transition-opacity disabled:opacity-60"
        style={{ backgroundColor: CIEN_CUADRAS_BUTTON_BLUE }}
      >
        {isSubmitting ? <SpinnerIcon className="w-5 h-5 text-white mr-2" /> : null}
        Validar y Enviar Enlace
      </button>
      <button
        type="button"
        onClick={onCancel}
        className="w-full text-xs font-semibold text-gray-500 hover:text-gray-700 focus:outline-none py-1"
      >
        Volver a Iniciar Sesión
      </button>
    </form>
  );
};
